/*
    connect-classes.js

    Contains the following classes:

        BaseDerivedObject
        SearchObject
        ServiceObject
        ReviewObject - new 8/18 for editing Reviews

    usage:  search.taxonomies[tcare-taxonomy-1].forest.items

    Each contains a Taxonomies object which in turn contains Taxonomy and Forest objects.

*/

import { writable, get } from 'svelte/store';
import TaxonomiesObject, {TaxonomyObject, VIRTUAL} from "./taxonomy-classes";
import { TCARE_TAX_1, DEMOGRAPHICS, OPEN_ELIG_2022 } from "./taxonomy-classes";
import { searchServices2, publicSearchServices2} from '../api/services.ts';

import { SERVICE_NAME, DESCRIPTION, PHONE, FAX, EMAIL, OP_HOURS, COVER_AREAS, WEBSITE } from './constants.js';
import { ADDRESS_LINE1, CITY, STATE, POSTAL_CODE, COUNTY, COUNTRY } from './constants.js';
import { NECESSARY_DOCS, NECESSARY_MATS, INSURANCE_ACCEPT, NEXT_STEPS, COST_FREE } from './constants.js';
import { ELIGIBILITY, AVAILABILITY, LANGUAGES, REVIEWED, LAST_VALID } from './constants.js';
import { FACEBOOK, TWITTER, OTHER_SOCIAL, GOOGLE_BUS_LINK, GOOGLE_RATING, GOOGLE_NUM_RATINGS } from './constants.js';

import { level0Node, level1Node, level2Node, level3Node, level4Node } from '../stores/ResourceMenuStore.js';


// for looping taxonomies
const TAXONOMY_IDS = [TCARE_TAX_1, OPEN_ELIG_2022, DEMOGRAPHICS, VIRTUAL];

const DEFAULT_SEARCH_DISTANCE_UNIT = 'mi';
const DEFAULT_SEARCH_COUNTRY_CODE = 'us';
const DEFAULT_SEARCH_POSTAL_CODE = '';          // leave blank! (test with 63103, 70125 or similar...)

// Search and Service inherit from this
class BaseConnectObject {
    constructor (name) {
        this.name = name || 'no-instance-name';
        // create all keywords from master, no selected nodes
        this.taxonomies = new TaxonomiesObject();
        //this.demographicsCopy = new TaxonomyObject('demographicsCopy');     // new 7/1
    }

    // must be called after loading API data
    // we must make copies here or all will still reference same objects!
    // WARNING - any future taxonomies must be included here (and elsewhere)
    copyTaxonomies(taxonomies) {
        if (!taxonomies) return;

        for (const taxId of TAXONOMY_IDS) {     //[TCARE_TAX_1, OPEN_ELIG_2022, DEMOGRAPHICS, VIRTUAL]
            if (taxonomies[taxId]) {
                this.taxonomies[taxId].copyToForestItems(taxonomies[taxId]);
            }    
        }
    }

    // 7/1 - copies the current snapshot into the demographicsCopy TaxonomyObject
    // cloneDemographics() {
    //     this.demographicsCopy.copyToForestItems(this.taxonomies[DEMOGRAPHICS]);
    // }

    // isDemographicsChanged() {
    //     const keywords1 = this.taxonomies[DEMOGRAPHICS].getSelectedKeywords();
    //     const keywords2 = this.demographicsCopy.getSelectedKeywords();
    //     // first easy check
    //     if (keywords1.length !== keywords2.length) return true;
    //     // compare words
    //     for (let i = 0; i < keywords1.length; i++) {
    //         if (keywords1[i] !== keywords2[i]) return true;
    //     }
    //     return false;
    // }

    // cannot use the constants in ids
    formatTaxonomiesForApi() {
        // todo: convert virtual input to virtual tax
        let taxonomies = {
            'tcare-taxonomy-1': this.taxonomies[TCARE_TAX_1].getSelectedKeywords(),
            'open-eligibility-2022': this.taxonomies[OPEN_ELIG_2022].getSelectedKeywords(),
            'demographics': this.taxonomies[DEMOGRAPHICS].getSelectedKeywords(),
            'virtual': this.taxonomies[VIRTUAL].getSelectedKeywords(),
        }
        return taxonomies;
    }

    // similar to formatTaxonomiesForApi()
    flattenTaxonomiesNodesToApiData() {
        this.apiData.taxonomies = this.formatTaxonomiesForApi();
    }

    getFlattenedTaxonomies() {
        return this.formatTaxonomiesForApi();
    }


    clearTaxonomies() {
        this.taxonomies[TCARE_TAX_1].selectAllNodes(false);
        this.taxonomies[OPEN_ELIG_2022].selectAllNodes(false);
        this.taxonomies[DEMOGRAPHICS].selectAllNodes(false);
        // new 10/17
        this.taxonomies[DEMOGRAPHICS].filterAllNodes(false);

        // this is kind of a hack for the TaxonomyMenu
        this.taxonomies[OPEN_ELIG_2022].currentNode0 = null;
        this.taxonomies[OPEN_ELIG_2022].currentNode1 = null;
        this.taxonomies[OPEN_ELIG_2022].currentNode2 = null;
        this.taxonomies[OPEN_ELIG_2022].currentNode3 = null;
        this.taxonomies[OPEN_ELIG_2022].currentNode4 = null;

        // ResourceMenuStore
        level0Node.set(null);
        level1Node.set(null);
        level2Node.set(null);
        level3Node.set(null);
        level4Node.set(null);
    }

    // these get() functions all return flat arrays

    getAllNodes() {
        return this.taxonomies.getAllNodes();
    }
    
    getSelectedNodes() {
        return this.taxonomies.getSelectedNodes();
    }

    selectAllNodes(isSelected) {
        this.taxonomies.selectAllNodes(isSelected);
    }

}

// SEARCH OBJECT

export class SearchObject extends BaseConnectObject {
    constructor (name) {
        super(name);

        // props unique to Searches
        this.distance = 5;
        this.distanceUnit = DEFAULT_SEARCH_DISTANCE_UNIT;
        this.text = "";
        this.virtualOnly = false;
        this.exclusiveRequirements = [];
        this.location = {
            country: DEFAULT_SEARCH_COUNTRY_CODE,
            postalCode: DEFAULT_SEARCH_POSTAL_CODE
        };

        // these arrays get populated after searches
        this.postalResults = [];            // main result set, also used for TaxonomyMenu
        this.filterResults = [];            // after choosing filters or free text
    }

    // this is never called!  12/01
    clearSearch() {
        this.distance = 5;
        this.distanceUnit = DEFAULT_SEARCH_DISTANCE_UNIT;
        this.text = "";
        this.virtualOnly = false;
        this.exclusiveRequirements = [];
        this.location = {
            country: DEFAULT_SEARCH_COUNTRY_CODE,
            postalCode: DEFAULT_SEARCH_POSTAL_CODE
        };
        this.clearTaxonomies();
    }

    formatSearchFiltersForApi() {
        const filters = {};
        filters.taxonomies = this.formatTaxonomiesForApi();
        filters.distance = this.distance + this.distanceUnit;
        filters.text = this.text;
        filters.virtualOnly = this.virtualOnly;
        filters.location = this.location;
        filters.exclusiveRequirements = this.exclusiveRequirements;
        filters.operator = 'and';
        // plus others...
        return filters;
    }

    // not used
    getFormattedBody() {
        return this.formatSearchFiltersForApi();
    }

    // new 10/17 - constructed after a filter search
    // mark all the nodes .isFiltered
    getAllResultsDemographicsKeywords(results) {
        const demoKeywords = [];
        let svcDemoKeywords = null;

        if (!results) return demoKeywords;

        // get all the demographic keywords in all the results
        for (const service of results) {
            if (service.taxonomies) {
                svcDemoKeywords = service.taxonomies[DEMOGRAPHICS];
                if (svcDemoKeywords) {
                    for (const keyword of svcDemoKeywords) {
                        if (!demoKeywords.includes(keyword)) {          // no dupes!
                            demoKeywords.push(keyword);
                        }
                    }
                }
            }
        }

        return demoKeywords;
    }



    // called via SearchBar click or public page clicks
    // fills .postalResults[] with index records
    async runSearch(size, isPublic = false) {
        let resp = null;
        this.postalResults = [];
        this.filterResults = [];
        let body = this.getFormattedBody();
        body.size = size;
        body.strict = false;

        if (isPublic) {
            resp = await publicSearchServices2(body);
        } else {
            resp = await searchServices2(body);
        }

        if (resp.errors) {
            return resp;                            // errors
        }

        this.postalResults = resp;
        return this.postalResults;              // could be an error object!
    }

    // fills .filterResults[] with index records (different than above)
    async runFilterSearch(size, isPublic = false) {
        // console.log('SearchObject runFilterSearch');

        // 12/2 - must do this before getFormattedBody()
        this.taxonomies[DEMOGRAPHICS].selectAllNodes(false);
        this.taxonomies[DEMOGRAPHICS].filterAllNodes(false);

        let resp = null;
        this.filterResults = [];
        let body = this.getFormattedBody();         // alternate call
        body.size = size;
        body.strict = true;

        if (isPublic) {
            resp = await publicSearchServices2(body);
        } else {
            resp = await searchServices2(body);
        }

        if (resp.errors) {
            return resp;                            // errors
        }

        this.filterResults = resp;

        // new 10/17 use only the demographics in the filterResults - for checkboxes and chips
        let resultsKeywords = this.getAllResultsDemographicsKeywords(this.filterResults);

        // set node.isFiltered = true if in keywords array
        this.taxonomies[DEMOGRAPHICS].filterByKeywords(resultsKeywords);

        return this.filterResults;              // could be an error object!
    }


    // new 10/17 - IMPORTANT - does NOT alter the filtered demographics
    async runDemographicSearch(size, isPublic = false) {
        let resp = null;
        this.filterResults = [];
        let body = this.getFormattedBody();         // alternate call
        body.size = size;
        body.strict = true;

        if (isPublic) {
            resp = await publicSearchServices2(body);
        } else {
            resp = await searchServices2(body);
        }

        if (resp.errors) {
            return resp;                            // errors
        }

        this.filterResults = resp;

        // does NOT alter the filtered demographics here

        return this.filterResults;              // could be an error object!
    }

    // new 11/24 - IMPORTANT - does NOT alter the filtered demographics
    async runSortSearch(size, isPublic = false, sortField, isAscending) {
        let resp = null;
        this.filterResults = [];
        let body = this.getFormattedBody();         // alternate call
        body.size = size;
        body.strict = true;

        body.sortBy = sortField;                     // set the sort params
        body.sortAsc = isAscending;

        if (isPublic) {
            resp = await publicSearchServices2(body);
        } else {
            resp = await searchServices2(body);
        }

        if (resp.errors) {
            return resp;                            // errors
        }

        this.filterResults = resp;

        // does NOT alter the filtered demographics here

        return this.filterResults;              // could be an error object!
    }


    // new 11/14
    getFilterResultsIds() {
        return this.filterResults.map(item => item['service-id']);
    }

}


// SERVICE OBJECT

export class ServiceObject extends BaseConnectObject {
    constructor (name) {
        super(name);
        // props unique to Services
        this.apiData = {
            [SERVICE_NAME]: '',
            [DESCRIPTION]: '',
            [PHONE]: null,
            [FAX]: null,
            [EMAIL]: null,
            [OP_HOURS]: null,
            [COVER_AREAS]: null,
            [WEBSITE]: null,

            [ADDRESS_LINE1]: null,
            [CITY]: null,
            [STATE]: null,
            [POSTAL_CODE]: null,
            [COUNTY]: null,
            [COUNTRY]: null,

            [NECESSARY_DOCS]: null,
            [NECESSARY_MATS]: null,
            [INSURANCE_ACCEPT]: null,
            [NEXT_STEPS]: null,
            [COST_FREE]: null,
            [ELIGIBILITY]: null,
            [AVAILABILITY]: null,
            [LANGUAGES]: null,
            [REVIEWED]: null,
            [LAST_VALID]: null,

            [FACEBOOK]: null,
            [TWITTER]: null,
            [OTHER_SOCIAL]: null,
            [GOOGLE_BUS_LINK]: null,
            [GOOGLE_RATING]: null,
            [GOOGLE_NUM_RATINGS]: null,
        };

        this.apiData.taxonomies = {
            [TCARE_TAX_1]: null,
            [DEMOGRAPHICS]: null,
            [OPEN_ELIG_2022]: null,
            [VIRTUAL]: null
        }

        this.currentLat = 0.00;
        this.currentLon = 0.00;
    }

    // only if the props need to retail blank values...
    // this is where a model would be used (ProtoBuf?)
    clearApiData() {
        if (!this.apiData) return;

        // this is the "model" defined in the API data
        this.apiData[SERVICE_NAME] = '';
        this.apiData[DESCRIPTION] = '';
        this.apiData[PHONE] = null;
        this.apiData[FAX] = null;
        this.apiData[EMAIL] = null;
        this.apiData[OP_HOURS] = null;
        this.apiData[COVER_AREAS] = null;
        this.apiData[WEBSITE] = null;

        this.apiData[ADDRESS_LINE1] = null;
        this.apiData[CITY] = null;
        this.apiData[STATE] = null;
        this.apiData[POSTAL_CODE] = null;
        this.apiData[COUNTY] = null;
        this.apiData[COUNTRY] = null;

        this.apiData[NECESSARY_DOCS] = null;
        this.apiData[NECESSARY_MATS] = null;
        this.apiData[INSURANCE_ACCEPT] = null;
        this.apiData[NEXT_STEPS] = null;
        this.apiData[COST_FREE] = null;
        this.apiData[ELIGIBILITY] = null;
        this.apiData[AVAILABILITY] = null;
        this.apiData[LANGUAGES] = null;
        this.apiData[REVIEWED] = null;
        this.apiData[LAST_VALID] = null;

        this.apiData[FACEBOOK] = null;
        this.apiData[TWITTER] = null;
        this.apiData[OTHER_SOCIAL] = null;
        this.apiData[GOOGLE_BUS_LINK] = null;
        this.apiData[GOOGLE_RATING] = null;
        this.apiData[GOOGLE_NUM_RATINGS] = null;

        this.selectAllNodes(false);
        // this.updateTaxonomiesNodesFromApiData();            // ???
    }

    setApiData(data) {
        this.apiData = data;
    }

    copyApiDataFrom(data) {
        this.clearApiData();
        this.apiData = JSON.parse(JSON.stringify(data));
        this.updateTaxonomiesNodesFromApiData();
    }

    // new 8/19 - processes only one taxonomy at a time
    findAndSelectTaxonomyObjTreeNodes(taxId, keywords) {
        const taxonomy = this.taxonomies[taxId];
        if (!taxonomy) return;
        for (const keyword of keywords) {
            let node = taxonomy.findNodeByKeyword(keyword);
            if (node) node.isSelected = true;
        }
    }

    // called by Service's copyApiDataFrom()
    // creates flat array of all incoming keywords and selects them in trees
    updateTaxonomiesNodesFromApiData() {
        let apiKeywords = [];
        if (!this.apiData || !this.apiData.taxonomies) return;

        for (const taxId of TAXONOMY_IDS) {
            apiKeywords = [];
            if (this.apiData.taxonomies[taxId]) {
                for (const keyword of this.apiData.taxonomies[taxId]) {
                    apiKeywords.push(keyword);
                }
                this.findAndSelectTaxonomyObjTreeNodes(taxId, apiKeywords);
            }    
        }
    }

}



// DOES NOT extend BaseConnectObject - no super
export class ReviewObject {
    constructor (name) {
        this.name = name;
        this.apiData = {};

        // left will need to be set to only one leftServices[] object at a time
        this.leftServiceObj = writable(new ServiceObject('leftServiceObj'));        // Svelte store
        this.rightServiceObj = writable(new ServiceObject('rightServiceObj'));      // Svelte store
    }

    copyReview(review) {
        this.clearReview();
        this.apiData = review;

        if (this.apiData.leftServices && this.apiData.leftServices.length > 0) {
            get(this.leftServiceObj).copyApiDataFrom(this.apiData.leftServices[0]);
        }

        if (this.apiData.rightService) {
            get(this.rightServiceObj).copyApiDataFrom(this.apiData.rightService);
        }
    }

    clearReview() {
        this.apiData = {};
        get(this.rightServiceObj).clearApiData();
    }

    /*
        8/22 new API format per Vito
        {
            ids: ["123"],                       // review ids
            containsReviewerInput: true,
            finalServices: {                    // map of review ids to service data
                "123": {                        // will only be one - no multi editing
                    id: 123131,
                    serviceName: "abcco",       // plus rest of fields...
                }
            }
        }
    */   
    formatReviewApiBody(isEdited) {
        let reviewId = this.apiData.id;
        let body = {
            ids: [reviewId]                                 // taskId (not serviceId)
        };

        if (isEdited) {
            let rightStore = get(this.rightServiceObj);     // deferenced Service object
            let finalService = rightStore.apiData;
            if (finalService) {
                finalService.taxonomies = rightStore.formatTaxonomiesForApi();
            }
            body.containsReviewerInput = true,
            body.finalServices = {
                [reviewId]: finalService                    // Vito likes his numeric props :)
            }            
        }

        return body;
    }

}

